import { TemplateResult } from "lit-html";
import JrModal from "@libs/modal/index";
import { focusHandler, isValidEmail } from "@utils/index";
import { getDomain } from "@services/email-service";
import { JtagService } from "@services/jtag-service";
import { emitAngularEvent } from "@services/event-service";
import { getWhat, getWhere } from "@services/search-service";
import { findProvider } from "@services/link-to-inbox-service";
import { pushEvent } from "@services/gtm-service";
import { addMySearches, subscribeJobseeker } from "@services/xhr-service";
import ModalProposalTemplate from "../../templates/favourite";
import { proposalConfigs } from "../../configs";

export default class FavouriteJobsProposalModal extends JrModal {
  private loading: boolean;
  private config: ProposalConfigs;
  private formData: IProposalFormData;
  private formSubmitted: boolean = false;
  private linkToInboxProviderUrl: string = '';
  private captchaEnabled: boolean;
  private captchaSiteKey: string;
  private captchaResponse: string = '';
  private captchaInstance: number = -1;
  private socialCaptchaInstance: number = -1;
  private socialCaptchaResponse: string = '';
  private socialSubscriptionError: boolean = false;
  private socialButtonClassName: string = '';
  private jobseekerFirstName: string = '';
  private jobseekerLastName: string = '';
  private modalState: 'proposal' | 'welcome' | 'mySearches' | 'error' = 'proposal';

  constructor(
    readonly customModalClass: string,
    readonly wrapperClass: string,
    readonly jrConfigGlobal: JrConfigGlobal,
  ) {
    super(customModalClass, wrapperClass, jrConfigGlobal);

    this.config = proposalConfigs(jrConfigGlobal);
    this.loading = false;
    this.formData = this.getFormData(jrConfigGlobal);
    this.captchaEnabled = this.jrConfigGlobal.captcha.enabled;
    this.captchaSiteKey = this.jrConfigGlobal.captcha.siteKey;
  }


  handleEmailChange = (value: string): void => {
    this.setEmail(value);

    this.render();
  }

  handleSubmit = async (event: Event) => {
    event.preventDefault();

    const { email }: IProposalModalFormElements = <IProposalModalFormElements>(<HTMLFormElement>event.target);

    this.setEmail(email.value);
    this.setFormSubmitted(true);

    this.render();

    if (this.isValidform()) {
      if (this.captchaEnabled && this.captchaInstance !== -1) {
        grecaptcha.ready(() => {
          grecaptcha.execute(this.captchaInstance);
        })
      } else {
        await this.registerJobseeker();
      }
    }
  }

  handleMySearchesSubmit = async (event: Event) => {
    event.preventDefault();
    const formElements: ProposalMySearchFormElements = <ProposalMySearchFormElements>(<HTMLFormElement>event.target);
    const formData: IAddMySearches = {
      mySearches: [{
        what: formElements.what1.value,
        where: formElements.where1.value
      }, {
        what: formElements.what2.value,
        where: formElements.where2.value
      }],
      email: this.formData.email?.value || ''
    };

    try {
      const response = await addMySearches(formData);

      if (this.isResponseSuccessful(response)) {
        this.modalState = 'welcome';
      } else {
        this.modalState = 'error';
      }
      this.render();
    } catch (_) {
      this.modalState = 'error';
      this.render();
    }
  }

  handleOneSearchSubmit = async (): Promise<void> => {
    const data: IAddMySearches = {
      mySearches: [{
        what: this.formData.what.value,
        where: this.formData.where.value
      }],
      email: this.formData.email?.value || ''
    };

    try {
      const response = await addMySearches(data);

      if (this.isResponseSuccessful(response)) {
        this.modalState = 'welcome';
      } else {
        this.modalState = 'error';
      }
      this.render();
    } catch (_) {
      this.modalState = 'error';
      this.render();
    }
  }

  handleSocialSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    this.setLoading(true);
    this.render();

    const { className } = (<HTMLElement>event.target);
    this.setSocialButtonClassName(className);

    if (this.captchaEnabled && this.socialCaptchaInstance !== -1) {
      grecaptcha.ready(() => grecaptcha.execute(this.socialCaptchaInstance))
    } else {
      await this.handleSocialLogin();
    }
  }

  getContent = async (): Promise<TemplateResult> => ModalProposalTemplate(this.config, this.getModel());

  openedCallback = (): void => {
    if (this.captchaEnabled) {
      const interval = setInterval(() => {
        if (window.grecaptcha) {
          grecaptcha.ready(() => {
            this.initializeCaptchaInstance();
            if (this.jrConfigGlobal.socialLogin.enabled && this.jrConfigGlobal.socialLogin.insidePopupEnabled) {
              this.initializeSocialCaptchaInstance();
            }
          })
          clearInterval(interval);
        }
      }, 100);
    }
  }

  private initializeCaptchaInstance = () => {
    this.captchaInstance = window.grecaptcha.render('modal-captcha-container', {
      sitekey: this.captchaSiteKey, size: "invisible", badge: 'inline', callback: async (response) => {
        if (response) {
          this.setCaptchaResponse(response);
          await this.registerJobseeker();
        } else {
          this.handleCaptchaError();
        }
      }, 'error-callback': this.handleCaptchaError, 'expired-callback': this.handleCaptchaError
    });
  }

  private initializeSocialCaptchaInstance = () => {
    this.socialCaptchaInstance = window.grecaptcha.render('modal-social-captcha-container', {
      sitekey: this.captchaSiteKey, size: "invisible", badge: 'inline', callback: (response) => {
        if (response) {
          this.setSocialCaptchaResponse(response);
          this.handleSocialLogin();
        } else {
          this.handleSocialCaptchaError();
        }
      }, 'error-callback': this.handleSocialCaptchaError, 'expired-callback': this.handleSocialCaptchaError
    });
  }

  private getModel = (): IProposalModel => ({
    handleSubmit: this.handleSubmit,
    handleSocialSubmit: this.handleSocialSubmit,
    handleMySearchesSubmit: this.handleMySearchesSubmit,
    handleOneSearchSubmit: this.handleOneSearchSubmit,
    handleEmailChange: this.handleEmailChange,
    close: this.close,
    loading: this.loading,
    formData: this.formData,
    formSubmitted: this.formSubmitted,
    modalState: this.modalState,
    linkToInboxProviderUrl: this.linkToInboxProviderUrl,
    socialSubscriptionError: this.socialSubscriptionError
  });

  private trackWhereByIp = (): void => {
    if (this.jrConfigGlobal.feature.locationFieldInModalPopupEnabled && !this.config.isEmptySearch) {
      JtagService.trackCustomEvent('subscription_modal_where_by_ip_input_value', {
        event_label: this.formData.where.value ? 'filled' : 'empty'
      });
    }
  }

  private setLoading = (loading: boolean): void => {
    this.loading = loading;
  }

  private setLinkToInboxProviderUrl = (url: string): void => {
    this.linkToInboxProviderUrl = url;
  }

  private setFormSubmitted = (isSubmitted: boolean): void => {
    this.formSubmitted = isSubmitted;
  }

  private setCaptchaResponse = (captchaResponse: string): void => {
    this.captchaResponse = captchaResponse;
  }

  private getFormData = (jrConfigGlobal: JrConfigGlobal = window.jrConfigGlobal): IProposalFormData => {
    const {
      search: {
        where: { resolvedByIp },
        radius: { value: radiusValue }
      }
    } = jrConfigGlobal;

    return {
      what: {
        value: getWhat(jrConfigGlobal),
      },
      where: {
        value: resolvedByIp || getWhere(jrConfigGlobal),
        isResolvedByIp: resolvedByIp.length > 0
      },
      email: {
        value: '',
        isValid: false
      },
      radius: {
        value: radiusValue
      }
    }
  }


  private setEmail = (value: string): void => {
    this.formData.email = {
      value,
      isValid: isValidEmail(value),
    }
  }

  private setJobseekerFirstName = (value: string): void => {
    this.jobseekerFirstName = value;
  }

  private setJobseekerLastName = (value: string): void => {
    this.jobseekerLastName = value;
  }

  private setSocialCaptchaResponse = (socialCaptchaResponse: string): void => {
    this.socialCaptchaResponse = socialCaptchaResponse;
  }

  private setSocialButtonClassName = (socialButtonClassName: string): void => {
    this.socialButtonClassName = socialButtonClassName;
  }

  private isValidform = (): boolean =>
    !!this.formData.email?.isValid
    && (this.formData.what.value.length > 0 || this.formData.where.value.length > 0);

  private isResponseSuccessful = (response: Response): boolean => response.status >= 200 && response.status < 300;

  private isResponseConflict = (response: Response): boolean => response.status === 409;

  private sendData = async (data: ISubscribe) => {
    try {
      const response: Response = await subscribeJobseeker(data);
      const jobseekerCreated = this.isResponseSuccessful(response);

      pushEvent({
        event: 'jobseekerCreate',
        jobseekerCreated,
        subscriberUid: response.headers.get('subscriberUid'),
        subscriptionConversionValue: parseFloat(response.headers.get('subscriptionConversionValue') || '0')
      });

      this.setLoading(false);
      if (jobseekerCreated || response.status === 409) {
        this.handleAfterSubscription();
      } else {
        this.modalState = 'error';
        this.render();
      }
    } catch (error) {
      pushEvent({
        event: 'jobseekerCreate',
        jobseekerCreated: false,
        subscriberUid: undefined,
        subscriptionConversionValue: undefined
      });

      this.setLoading(false);
      this.modalState = 'error';
      this.render();
    }
  }

  private handleAfterSocialSubscription = async (socialSource: string): Promise<void> => {
    try {
      if (this.isValidform()) {
        const data: ISubscribe = {
          source: socialSource,
          referrer: 'pop-up',
          lastname: this.jobseekerLastName,
          firstname: this.jobseekerFirstName,
          what: this.formData.what.value,
          where: this.formData.where.value,
          email: this.formData.email?.value || '',
          radius: this.formData.radius?.value || '',
          captchaResponse: this.socialCaptchaResponse
        };

        const response: Response = await subscribeJobseeker(data);
        const jobseekerCreated = this.isResponseSuccessful(response);
        const jobseekerConflict = this.isResponseConflict(response);
        const redirectUrl = response.headers.get('activateUrl');

        if (jobseekerCreated) {
          JtagService.trackSubscription(`signup_modal_${socialSource}`, getDomain(this.formData.email?.value || '') || '');

          if (redirectUrl && (jobseekerCreated || jobseekerConflict)) {
            window.location.replace(redirectUrl);
          }
        }
      } else {
        this.handleSocialSubscriptionError();
      }
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleLinkedinLogin = (): void => {
    // TODO
    // handleLinkedinLogin
  }

  private handleGoogleLogin = async (): Promise<void> => {
    try {
      const googleUser = await window.auth2.signIn({ scope: 'profile email' });
      const userProfile = googleUser.getBasicProfile();

      const email = userProfile.getEmail() || '';
      const firstName = userProfile.getGivenName() || '';
      const lastName = userProfile.getFamilyName() || '';

      this.setEmail(email);
      this.setJobseekerLastName(lastName);
      this.setJobseekerFirstName(firstName);

      await this.handleAfterSocialSubscription('google');
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleFacebookLogin = (): void => {
    try {
      window.FB.login(
        (loginResponse) => {
          if (loginResponse.status === 'connected') {
            window.FB.api('/me', { fields: ['email', 'first_name', 'last_name'] }, async (apiResponse) => {
              const {
                email = '',
                first_name: firstName = '',
                last_name: lastName = '',
              } = apiResponse;

              this.setEmail(email);
              this.setJobseekerLastName(lastName);
              this.setJobseekerFirstName(firstName);

              await this.handleAfterSocialSubscription('facebook');
            });
          } else {
            this.handleSocialSubscriptionError();
          }
        },
        {
          scope: 'public_profile, email',
          auth_type: 'rerequest',
        }
      );
    } catch (err) {
      this.handleSocialSubscriptionError();
    }
  }

  private handleSocialSubscriptionError = (): void => {
    this.setLoading(false);
    this.socialSubscriptionError = true;
    this.modalState = 'error';
    this.render();
  }

  private handleAfterSubscription() {
    JtagService.trackSubscription('modal_favourite', getDomain(this.formData.email?.value || '') || '');

    emitAngularEvent('subscription:sent', {
      type: 'modal',
      email: this.formData.email?.value,
    });

    this.hideNotificationBox();
    window.google.accounts.id.cancel();

    const linkToInboxProviders: LinkToInboxProvider[] = findProvider(this.formData.email?.value || '');

    if (linkToInboxProviders.length === 0) {
      this.modalState = 'mySearches'
      this.render();
      focusHandler('.js-mysearch-what1');
    } else {
      this.modalState = 'welcome';
      this.setLinkToInboxProviderUrl(linkToInboxProviders[0].url);
      this.render();
    }
  }

  private handleCaptchaError() {
    window.grecaptcha.reset(this.captchaInstance);
    this.setLoading(false);
    this.render();
  }

  private handleSocialCaptchaError() {
    window.grecaptcha.reset(this.socialCaptchaInstance);
    this.setLoading(false);
    this.render();
  }

  private async registerJobseeker() {
    this.setLoading(true);
    this.render();

    const formData: ISubscribe = {
      what: this.formData.what.value,
      where: this.formData.where.value,
      email: this.formData.email?.value || '',
      radius: this.formData.radius?.value || '',
      captchaResponse: this.captchaResponse
    };
    this.trackWhereByIp();

    await this.sendData(formData);
  }

  private hideNotificationBox() {
    if (this.jrConfigGlobal.feature.notificationAlertBoxEnabled) {
      const notificationBox = <HTMLElement>document.querySelector('.js-notification-alert');
      notificationBox.classList.add('hide');
    }
  }

  private handleSocialLogin = async (): Promise<void> => {
    switch (true) {
      case this.socialButtonClassName.includes("ts-facebook"):
        this.handleFacebookLogin();

        break;
      case this.socialButtonClassName.includes("ts-linkedin"):
        this.handleLinkedinLogin();

        break;
      case this.socialButtonClassName.includes("ts-google"):
        await this.handleGoogleLogin();

        break;

      default:
        break;
    }
  }
}
